import {
  LuUserX,
  LuUserCheck,
  LuShoppingCart,
  LuWrench,
  LuFlame,
  LuBuilding2,
  LuShieldAlert,
  LuUserRound,
} from "react-icons/lu";
import { IoCheckmarkCircleOutline } from "react-icons/io5";

export const INCIDENT_TYPES = [
  {
    value: "CUSTOMER_INJURY",
    title: "Customer Injury",
    description: "Injuries to customers on the premises",
    icon: LuUserX,

    colors: {
      icon: "bg-red-50 text-red-500 border-red-100",
      button: "bg-red-600 hover:bg-red-700",
      focus: "focus:border-red-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: ["Dining Area", "Restroom", "Entrance/Exit", "Parking Lot"],
      },
      {
        name: "injuredPerson",
        label: "Injured Person",
        type: "select",
        required: true,
        options: ["Customer", "Visitor / Non-customer"],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder:
          "Describe what happened, how the injury occurred and type of injury.",
      },
      {
        name: "injuryType",
        label: "Injury Type",
        type: "select",
        required: true,
        options: [
          "Slip and Fall",
          "Burn",
          "Cut / Laceration",
          "Allergic Reaction",
        ],
      },
      {
        name: "immediateAction",
        label: "Immediate Action Taken",
        type: "textarea",
        required: false,
        placeholder: "What action was taken immediately?",
      },
    ],
  },

  {
    value: "STAFF_INJURY",
    title: "Staff Injury",
    description: "Injuries to staff during work",
    icon: LuUserCheck,

    colors: {
      icon: "bg-amber-50 text-amber-500 border-amber-100",
      button: "bg-amber-500 hover:bg-amber-600",
      focus: "focus:border-amber-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: [
          "Kitchen Main",
          "Prep Station",
          "Dining Floor",
          "Storage Room",
        ],
      },
      {
        name: "staffMember",
        label: "Staff Member",
        type: "select",
        required: true,
        options: [
          "John Doe (Chef)",
          "Jane Smith (Server)",
          "Mike Myers (Line Cook)",
        ],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder:
          "Describe what happened, how the injury occurred and type of injury.",
      },
      {
        name: "injuryType",
        label: "Injury Type",
        type: "select",
        required: true,
        options: [
          "Cut / Laceration",
          "Burn / Scald",
          "Strain / Sprain",
          "Slip / Trip",
        ],
      },
      {
        name: "bodyPart",
        label: "Body Part Affected",
        type: "select",
        required: false,
        options: [
          "Hand / Fingers",
          "Arm / Wrist",
          "Foot / Ankle",
          "Head / Face",
        ],
      },
      {
        name: "immediateAction",
        label: "Immediate Action Taken",
        type: "textarea",
        required: false,
        placeholder: "What action was taken immediately?",
      },
    ],
  },

  {
    value: "PROPERTY_DAMAGE",
    title: "Property Damage",
    description: "Damage to property or premises",
    icon: LuBuilding2,

    colors: {
      icon: "bg-indigo-50 text-indigo-500 border-indigo-100",
      button: "bg-indigo-600 hover:bg-indigo-700",
      focus: "focus:border-indigo-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: [
          "Main Dining Room",
          "Front Entrance",
          "Restroom Corridor",
          "Exterior Wall",
        ],
      },
      {
        name: "damageType",
        label: "Type of Damage",
        type: "select",
        required: true,
        options: [
          "Structural",
          "Furniture",
          "Plumbing / Water",
          "Broken Glass",
        ],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder: "Describe what was damaged and how it happened.",
      },
      {
        name: "estimatedCost",
        label: "Estimated Cost",
        type: "text",
        required: false,
        placeholder: "Enter estimated repair/replacement cost",
      },
    ],
  },

  {
    value: "SECURITY_INCIDENT",
    title: "Security Incident",
    description: "Theft, loss or security related incident",
    icon: LuShieldAlert,

    colors: {
      icon: "bg-orange-50 text-orange-500 border-orange-100",
      button: "bg-orange-600 hover:bg-orange-700",
      focus: "focus:border-orange-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: [
          "Front Entrance",
          "Main Dining Area",
          "Parking Lot",
          "Storage Area",
        ],
      },
      {
        name: "securityType",
        label: "Security Incident Type",
        type: "select",
        required: true,
        options: [
          "Theft",
          "Lost Property",
          "Unauthorized Access",
          "Suspicious Activity",
        ],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder: "Describe what happened.",
      },
      {
        name: "impact",
        label: "Impact",
        type: "text",
        required: false,
        placeholder: "What was the impact?",
      },
      {
        name: "immediateAction",
        label: "Immediate Action Taken",
        type: "textarea",
        required: false,
        placeholder: "What action was taken?",
      },
    ],
  },

  {
    value: "FOOD_SPILL_CONTAMINATION",
    title: "Food Spill / Contamination",
    description: "Spills, contamination or cross contamination",
    icon: LuShoppingCart,

    colors: {
      icon: "bg-emerald-50 text-emerald-500 border-emerald-100",
      button: "bg-emerald-600 hover:bg-emerald-700",
      focus: "focus:border-emerald-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: [
          "Kitchen Main",
          "Buffet Counter",
          "Dining Area",
          "Walk-in Freezer",
        ],
      },
      {
        name: "type",
        label: "Type",
        type: "select",
        required: true,
        options: [
          "Major Spill",
          "Cross Contamination",
          "Expired Product",
          "Foreign Object",
        ],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder:
          "Describe what happened, what was spilled/contaminated and volume.",
      },
      {
        name: "foodItemInvolved",
        label: "Food Item Involved",
        type: "text",
        required: false,
        placeholder: "What food item was involved?",
      },
      {
        name: "immediateAction",
        label: "Immediate Action Taken",
        type: "textarea",
        required: false,
        placeholder: "How was the issue handled?",
      },
    ],
  },

  {
    value: "ALLERGEN_DIETARY_ISSUE",
    title: "Allergen / Dietary Issue",
    description: "Allergen exposure or dietary concern",
    icon: LuUserRound,

    colors: {
      icon: "bg-teal-50 text-teal-500 border-teal-100",
      button: "bg-teal-600 hover:bg-teal-700",
      focus: "focus:border-teal-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: ["Dining Area", "Kitchen", "Buffet Counter", "Takeaway Area"],
      },
      {
        name: "allergen",
        label: "Allergen / Dietary Issue",
        type: "select",
        required: true,
        options: ["Peanuts", "Tree Nuts", "Dairy", "Gluten", "Egg", "Other"],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder: "Describe the allergen or dietary issue.",
      },
      {
        name: "immediateAction",
        label: "Immediate Action Taken",
        type: "textarea",
        required: false,
        placeholder: "What action was taken?",
      },
    ],
  },

  {
    value: "EQUIPMENT_FAILURE",
    title: "Equipment Failure",
    description: "Failure or breakdown of equipment",
    icon: LuWrench,

    colors: {
      icon: "bg-blue-50 text-blue-600 border-blue-100",
      button: "bg-blue-600 hover:bg-blue-700",
      focus: "focus:border-blue-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: [
          "Main Kitchen",
          "Bar Area",
          "Server Station",
          "Walk-in Cooler",
        ],
      },
      {
        name: "equipment",
        label: "Equipment",
        type: "select",
        required: true,
        options: [
          "POS Terminal 1",
          "Walk-in Refrigerator",
          "Commercial Dishwasher",
          "Deep Fryer B",
        ],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder: "Describe the issue with the equipment.",
      },
      {
        name: "impact",
        label: "Impact",
        type: "text",
        required: false,
        placeholder: "How did this impact operations?",
      },
      {
        name: "immediateAction",
        label: "Immediate Action Taken",
        type: "text",
        required: false,
        placeholder: "What action was taken?",
      },
    ],
  },

  {
    value: "CUSTOMER_COMPLAINT",
    title: "Customer Complaint",
    description: "Service, behaviour or other complaints",
    icon: LuUserRound,

    colors: {
      icon: "bg-purple-50 text-purple-500 border-purple-100",
      button: "bg-purple-600 hover:bg-purple-700",
      focus: "focus:border-purple-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: ["Dining Area", "Counter", "Entrance", "Other"],
      },
      {
        name: "complaintType",
        label: "Complaint Type",
        type: "select",
        required: true,
        options: [
          "Service",
          "Food Quality",
          "Staff Behaviour",
          "Cleanliness",
          "Other",
        ],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder: "Describe the customer complaint.",
      },
      {
        name: "immediateAction",
        label: "Immediate Action Taken",
        type: "textarea",
        required: false,
        placeholder: "What action was taken?",
      },
    ],
  },

  {
    value: "FIRE_SAFETY_ISSUE",
    title: "Fire Safety Issue",
    description: "Fire, smoke, alarm or fire safety related",
    icon: LuFlame,

    colors: {
      icon: "bg-rose-50 text-rose-500 border-rose-100",
      button: "bg-rose-600 hover:bg-rose-700",
      focus: "focus:border-rose-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: [
          "Kitchen",
          "Dining Area",
          "Storage",
          "Electrical Room",
          "Other",
        ],
      },
      {
        name: "fireSafetyType",
        label: "Issue Type",
        type: "select",
        required: true,
        options: ["Fire", "Smoke", "Fire Alarm", "Fire Extinguisher", "Other"],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder: "Describe the fire safety issue.",
      },
      {
        name: "immediateAction",
        label: "Immediate Action Taken",
        type: "textarea",
        required: false,
        placeholder: "What action was taken?",
      },
    ],
  },

  {
    value: "OTHER_INCIDENT",
    title: "Other Incident",
    description: "Any other type of incident",
    icon: IoCheckmarkCircleOutline,

    colors: {
      icon: "bg-slate-50 text-slate-500 border-slate-100",
      button: "bg-slate-700 hover:bg-slate-800",
      focus: "focus:border-slate-500",
    },

    fields: [
      {
        name: "location",
        label: "Location",
        type: "select",
        required: true,
        options: [
          "Front Office",
          "Rear Alleyway",
          "Break Room",
          "Delivery Bay",
        ],
      },
      {
        name: "incidentType",
        label: "Incident Type",
        type: "select",
        required: true,
        options: [
          "General Disruption",
          "Vandalism",
          "IT System Down",
          "Miscellaneous",
        ],
      },
      {
        name: "description",
        label: "Description",
        type: "textarea",
        required: true,
        placeholder: "Describe what happened.",
      },
      {
        name: "impact",
        label: "Impact",
        type: "text",
        required: false,
        placeholder: "What was the impact?",
      },
      {
        name: "immediateAction",
        label: "Immediate Action Taken",
        type: "textarea",
        required: false,
        placeholder: "What action was taken?",
      },
    ],
  },
];
